(() => {
  'use strict';

  const body = document.body;
  if (!body || body.dataset.storeAppEnabled !== 'true') return;

  const baseUrl = body.dataset.storeAppBaseUrl || '';
  const defaultPath = body.dataset.storeAppApiPath || '/api/shopify/content';
  const cacheMinutes = Math.max(0, Number(body.dataset.storeAppCacheMinutes || 0));
  const allowHtml = body.dataset.storeAppAllowHtml === 'true';

  const safeUrl = (value, relativeToStoreApp = false) => {
    if (!value) return '';
    try {
      const origin = relativeToStoreApp ? (baseUrl || window.location.origin) : window.location.origin;
      const url = new URL(value, origin);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      return url.toString();
    } catch (_) { return ''; }
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  };

  const cacheKey = (url) => `mp-store-app:${url}`;
  const readCache = (url) => {
    if (!cacheMinutes) return null;
    try {
      const parsed = JSON.parse(localStorage.getItem(cacheKey(url)) || 'null');
      if (!parsed || Date.now() - parsed.time > cacheMinutes * 60000) return null;
      return parsed.data;
    } catch (_) { return null; }
  };
  const writeCache = (url, data) => {
    if (!cacheMinutes) return;
    try { localStorage.setItem(cacheKey(url), JSON.stringify({ time: Date.now(), data })); } catch (_) {}
  };

  const makeUrl = (section) => {
    const path = section.dataset.endpointPath || defaultPath;
    const url = new URL(path, baseUrl);
    url.searchParams.set('slot', section.dataset.slot || 'content');
    url.searchParams.set('locale', document.documentElement.lang || 'en');
    url.searchParams.set('shop', window.Shopify?.shop || window.location.hostname);
    url.searchParams.set('section_id', section.dataset.sectionId || '');
    if (section.dataset.resourceKey) url.searchParams.set('resource', section.dataset.resourceKey);
    return url.toString();
  };

  const appendButton = (parent, item, secondary = false) => {
    const label = secondary ? item.secondary_button_label : (item.button_label || item.link_label);
    const rawUrl = secondary ? item.secondary_button_url : (item.button_url || item.url);
    const href = safeUrl(rawUrl);
    if (!label || !href) return;
    const link = el('a', `btn ${secondary ? 'mp-btn-light' : 'mp-btn-red'}`);
    link.href = href;
    link.textContent = label;
    if ((secondary ? item.secondary_button_target : item.button_target) === '_blank') {
      link.target = '_blank'; link.rel = 'noopener noreferrer';
    }
    parent.appendChild(link);
  };

  const normalizePosition = (value, fallback = 'middle-left') => {
    const allowed = new Set(['top-left','top-right','middle-left','middle-right','bottom-left','bottom-right']);
    const normalized = String(value || '').trim().toLowerCase();
    return allowed.has(normalized) ? normalized : fallback;
  };

  const normalizeCssColor = (value, fallback = '') => {
    const color = String(value || '').trim();
    return /^#[0-9a-f]{3,8}$/i.test(color) ? color : fallback;
  };

  const normalizeTone = (mode, fallback = 'dark') => {
    const normalized = String(mode || '').trim().toLowerCase();
    if (normalized === 'hidden') return 'hidden';
    // Store App "whitebox" historically means dark copy over a light safe area.
    // Shopify deliberately removes the box itself and keeps only the dark copy.
    if (normalized === 'whitebox' || normalized === 'dark') return 'dark';
    if (normalized === 'plain' || normalized === 'light') return 'light';
    return fallback;
  };

  const renderSlideshow = (target, data, section) => {
    const slides = Array.isArray(data.slides) ? data.slides : (Array.isArray(data.items) ? data.items : []);
    if (!slides.length) return false;
    const id = `StoreAppCarousel-${section.dataset.sectionId || Math.random().toString(36).slice(2)}`;
    const carousel = el('div', 'carousel slide mp-hero-carousel');
    carousel.id = id;
    carousel.dataset.bsTouch = 'true';
    carousel.dataset.bsPause = 'hover';
    const inner = el('div', 'carousel-inner');
    const indicators = slides.length > 1 ? el('div', 'carousel-indicators mp-hero-indicators') : null;

    slides.forEach((slide, index) => {
      const item = el('div', `carousel-item${index === 0 ? ' active' : ''}`);
      const desktopPosition = normalizePosition(slide.text_position_desktop || slide.alignment, 'middle-left');
      const mobilePosition = normalizePosition(slide.text_position_mobile, 'bottom-left');
      const desktopTone = normalizeTone(slide.text_mode_desktop, 'custom');
      const mobileTone = normalizeTone(slide.text_mode_mobile, 'custom');
      const classes = [
        'mp-hero-slide',
        'mp-hero-tone-custom',
        'mp-hero-mobile-tone-custom',
        `mp-hero-position-${desktopPosition}`,
        `mp-hero-mobile-position-${mobilePosition}`
      ];
      if (desktopTone === 'hidden' || slide.show_text_desktop === false || Number(slide.show_text_desktop) === 0) classes.push('mp-hero-hide-desktop-copy');
      if (mobileTone === 'hidden' || slide.show_text_mobile === false || Number(slide.show_text_mobile) === 0) classes.push('mp-hero-hide-mobile-copy');
      const stage = el('article', classes.join(' '));
      const titleColor = normalizeCssColor(slide.title_color, normalizeCssColor(section.dataset.titleColor, '#FFFFFF'));
      const subtitleColor = normalizeCssColor(slide.subtitle_color, normalizeCssColor(section.dataset.subtitleColor, '#FF334F'));
      const introColor = normalizeCssColor(slide.intro_color || slide.introduction_color, normalizeCssColor(section.dataset.introColor, '#F4F5F6'));
      stage.style.setProperty('--mp-hero-title-color', titleColor);
      stage.style.setProperty('--mp-hero-subtitle-color', subtitleColor);
      stage.style.setProperty('--mp-hero-intro-color', introColor);
      const desktop = safeUrl(slide.image || slide.desktop_image || slide.image_url, true);
      const mobile = safeUrl(slide.mobile_image || slide.mobile_image_url, true);
      if (desktop) {
        const picture = el('picture', 'mp-hero-media');
        if (mobile) { const source = document.createElement('source'); source.media = '(max-width: 767px)'; source.srcset = mobile; picture.appendChild(source); }
        const image = document.createElement('img'); image.src = desktop; image.alt = slide.image_alt || slide.title || ''; image.loading = index === 0 ? 'eager' : 'lazy'; image.className = 'mp-hero-image'; if (index === 0) image.fetchPriority = 'high'; picture.appendChild(image); stage.appendChild(picture);
      } else {
        stage.classList.add('mp-hero-slide--fallback');
      }

      const content = el('div', 'mp-container mp-hero-content mp-hero-content--overlay');
      const copy = el('div', 'mp-hero-copy');
      const subtitle = slide.subtitle || slide.kicker || '';
      const heading = slide.title || slide.heading || '';
      const introduction = slide.introduction || slide.intro || slide.text || slide.summary || '';
      if (subtitle) copy.appendChild(el('p', 'mp-hero-subtitle', subtitle));
      if (heading) copy.appendChild(el(index === 0 ? 'h1' : 'h2', '', heading));
      if (introduction) copy.appendChild(el('p', 'mp-hero-text', introduction));
      const actions = el('div', 'mp-hero-actions'); appendButton(actions, slide, false); appendButton(actions, slide, true); if (actions.children.length) copy.appendChild(actions);
      content.appendChild(copy); stage.appendChild(content); item.appendChild(stage); inner.appendChild(item);

      if (indicators) {
        const indicator = el('button'); indicator.type = 'button'; indicator.dataset.bsTarget = `#${id}`; indicator.dataset.bsSlideTo = String(index); indicator.setAttribute('aria-label', `Slide ${index + 1}`); if (index === 0) { indicator.className = 'active'; indicator.setAttribute('aria-current', 'true'); } indicators.appendChild(indicator);
      }
    });
    if (indicators) carousel.appendChild(indicators);
    carousel.appendChild(inner);
    if (slides.length > 1) {
      ['prev','next'].forEach((direction) => {
        const button = el('button', `carousel-control-${direction} mp-hero-control`); button.type = 'button'; button.dataset.bsTarget = `#${id}`; button.dataset.bsSlide = direction;
        button.appendChild(el('span', `carousel-control-${direction}-icon`)); button.appendChild(el('span', 'visually-hidden', direction === 'prev' ? 'Previous' : 'Next')); carousel.appendChild(button);
      });
    }
    target.appendChild(carousel);
    if (window.bootstrap?.Carousel) new window.bootstrap.Carousel(carousel, { interval: Number(section.dataset.interval || 7000), ride: section.dataset.autoplay === 'true' ? 'carousel' : false, pause: 'hover', touch: true });
    return true;
  };

  const renderCards = (target, data, section) => {
    const items = Array.isArray(data.items) ? data.items : [];
    if (!items.length) return false;
    const columns = section.dataset.mpStoreApp === 'cards' ? 3 : 1;
    const row = el('div', 'row g-4');
    items.forEach((item) => {
      const col = el('div', `col-12 col-md-6 col-lg-${12 / columns}`);
      const card = el('article', 'mp-external-card h-100');
      const imageUrl = safeUrl(item.image || item.image_url || item.thumbnail, true);
      if (imageUrl) { const image = document.createElement('img'); image.src = imageUrl; image.alt = item.image_alt || item.title || ''; image.loading = 'lazy'; card.appendChild(image); }
      const content = el('div', 'mp-external-card__body');
      if (item.kicker) content.appendChild(el('span', 'mp-section-kicker', item.kicker));
      if (item.title) content.appendChild(el('h3', '', item.title));
      if (item.text || item.summary || item.introduction) content.appendChild(el('p', '', item.text || item.summary || item.introduction));
      const linkUrl = safeUrl(item.url || item.button_url);
      if (linkUrl) { const link = el('a', 'mp-arrow-link', item.button_label || item.link_label || 'Learn more'); link.href = linkUrl; content.appendChild(link); }
      card.appendChild(content); col.appendChild(card); row.appendChild(col);
    });
    target.appendChild(row); return true;
  };

  const renderContent = (target, data) => {
    if (Array.isArray(data.items) && data.items.length) return renderCards(target, data, { dataset: { columns: '1' } });
    const content = data.content || data.text || '';
    if (!content) return false;
    target.appendChild(el('div', 'rte mp-structured-content', content)); return true;
  };

  const renderTrustedHtml = (target, data) => {
    if (!allowHtml || !data.html) return false;
    target.innerHTML = data.html;
    return true;
  };

  const hydrate = async (section) => {
    const target = section.querySelector('.mp-external-target');
    const fallback = section.querySelector('.mp-external-fallback');
    const status = section.querySelector('.mp-external-status');
    if (!target || !baseUrl) return;
    const url = makeUrl(section);
    let data = readCache(url);
    try {
      if (!data) {
        const response = await fetch(url, { mode: 'cors', credentials: 'omit', headers: { 'Accept': 'application/json' } });
        if (!response.ok) throw new Error(`Store App returned ${response.status}`);
        data = await response.json(); writeCache(url, data);
      }
      target.replaceChildren();
      const mode = section.dataset.mpStoreApp;
      const rendered = mode === 'slideshow' ? renderSlideshow(target, data, section)
        : mode === 'cards' ? renderCards(target, data, section)
        : mode === 'trusted_html' ? renderTrustedHtml(target, data)
        : renderContent(target, data);
      if (rendered) { fallback?.setAttribute('hidden', 'hidden'); section.classList.add('is-hydrated'); if (status) status.textContent = ''; }
    } catch (error) {
      section.classList.add('has-external-error');
      if (status && window.Shopify?.designMode) status.textContent = `Store App content unavailable: ${error.message}`;
      console.warn('[Master Palm Store App content]', error);
    }
  };

  const start = () => document.querySelectorAll('[data-mp-store-app]').forEach(hydrate);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start, { once: true }) : start();
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll?.('[data-mp-store-app]').forEach(hydrate));
})();
